import { Injectable, signal, computed } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

// Software globals - converted from ActionScript SoftwareGlobals
export interface SoftwareConfig {
  version: string;
  ssl: boolean;
  defaultServer: string;
  defaultStorage: string;
  shareServer: string;
  defaultWorkers: string;
  desktop: boolean;
  debug: boolean;
  modulesUrl: string;
  brand: string;
  isDreambooksPro: boolean;
  isOnelabPro: boolean;
  isLfmPro: boolean;
  dreambooksApiToken?: string;
}

export interface LanguageOption {
  name: string;
  flag: string;
  locale: string;
}

@Injectable({
  providedIn: 'root'
})
export class GlobalsService {
  private _config = signal<SoftwareConfig>({
    version: '0.0.1',
    ssl: true,
    defaultServer: environment.apiBase,
    defaultStorage: 'storage.dreambooksworld.com',
    shareServer: 'editorpreview.com',
    defaultWorkers: '83.240.188.192',
    desktop: false,
    debug: false,
    modulesUrl: 'modules',
    brand: 'dreambookspro',
    isDreambooksPro: true,
    isOnelabPro: false,
    isLfmPro: false
  });

  // Public readonly signal
  readonly config = this._config.asReadonly();

  // User permissions
  private _isAdmin = new BehaviorSubject<boolean>(false);
  private _isSupport = new BehaviorSubject<boolean>(false);
  private _isDesigner = new BehaviorSubject<boolean>(false);
  private _isProduction = new BehaviorSubject<boolean>(false);

  // Brand availability
  private _hasDreambooksPro = new BehaviorSubject<boolean>(true);
  private _hasOneLabPro = new BehaviorSubject<boolean>(true);
  private _hasLfmPro = new BehaviorSubject<boolean>(true);

  // Observables
  isAdmin$ = this._isAdmin.asObservable();
  isSupport$ = this._isSupport.asObservable();
  isDesigner$ = this._isDesigner.asObservable();
  isProduction$ = this._isProduction.asObservable();
  hasDreambooksPro$ = this._hasDreambooksPro.asObservable();
  hasOneLabPro$ = this._hasOneLabPro.asObservable();
  hasLfmPro$ = this._hasLfmPro.asObservable();

  readonly languages: LanguageOption[] = [
    { name: 'Português - PT', flag: 'https://flagcdn.com/w40/pt.png', locale: 'pt_PT' },
    { name: 'Português - BR', flag: 'https://flagcdn.com/w40/br.png', locale: 'pt_BR' },
    { name: 'Español - ES', flag: 'https://flagcdn.com/w40/es.png', locale: 'es_ES' },
    { name: 'English - EN', flag: 'https://flagcdn.com/w40/us.png', locale: 'en_US' },
    { name: 'Français - FR', flag: 'https://flagcdn.com/w40/fr.png', locale: 'fr_FR' },
    { name: 'Deutsch - DE', flag: 'https://flagcdn.com/w40/de.png', locale: 'de_DE' },
    { name: 'Italiano - IT', flag: 'https://flagcdn.com/w40/it.png', locale: 'it_IT' },
    { name: 'Nederlands - NL', flag: 'https://flagcdn.com/w40/nl.png', locale: 'nl_NL' }
  ];

  get version(): string {
    return this._config().version;
  }

  set version(value: string) {
    this._config.update(cfg => ({ ...cfg, version: value }));
  }

  // AMFphp gateway endpoint
  // In development, use proxy to avoid CORS. In production, use direct URL.
  get amfGateway(): string {
    // Use relative URL for proxy in development
    if (this.isDevMode()) {
      return '/amfService.php';
    }
    const cfg = this._config();
    const protocol = cfg.ssl ? 'https' : 'http';
    return `${protocol}://${cfg.defaultServer}/amfService.php`;
  }

  // REST API endpoint (para chamadas HTTP convertidas do AMF)
  get servicesUrl(): string {
    const cfg = this._config();
    const protocol = cfg.ssl ? 'https' : 'http';
    return `${protocol}://${cfg.defaultServer}/api`;
  }

  // New Symfony REST API base URL (direct, no proxy)
  get apiBaseUrl(): string {
    return environment.apiUrl;
  }

  isDevMode(): boolean {
    return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  }

  get baseUrl(): string {
    const cfg = this._config();
    const protocol = cfg.ssl ? 'https' : 'http';
    return `${protocol}://${cfg.defaultServer}/`;
  }

  // Public web/site host (serves static pages like /conditions/{locale}.html
  // and /pricelist/{brand}). The API host carries an `api.` subdomain; the web
  // host is the same domain without it (api.express.dreambookspro.com ->
  // express.dreambookspro.com). When defaultServer is already the bare web host
  // (dev: express.dev.corp.lfmcorporate.com) the replace is a no-op.
  get webBaseUrl(): string {
    const cfg = this._config();
    const protocol = cfg.ssl ? 'https' : 'http';
    return `${protocol}://${cfg.defaultServer.replace(/^api\./, '')}/`;
  }

  get storageUrl(): string {
    const cfg = this._config();
    const protocol = cfg.ssl ? 'https' : 'http';
    return `${protocol}://${cfg.defaultStorage}/`;
  }

  get shareUrl(): string {
    const cfg = this._config();
    const protocol = cfg.ssl ? 'https' : 'http';
    return `${protocol}://${cfg.shareServer}/`;
  }

  get workersUrl(): string {
    return `http://${this._config().defaultWorkers}/lfm_orders/`;
  }

  get isAdmin(): boolean {
    return this._isAdmin.value;
  }

  get isSupport(): boolean {
    return this._isSupport.value;
  }

  get isDesigner(): boolean {
    return this._isDesigner.value;
  }

  get hasDreambooksPro(): boolean {
    return this._hasDreambooksPro.value;
  }

  get hasOneLabPro(): boolean {
    return this._hasOneLabPro.value;
  }

  get hasLfmPro(): boolean {
    return this._hasLfmPro.value;
  }

  setUserType(userType: number | string): void {
    // Ensure userType is a number (API sometimes returns string)
    const typeNum = Number(userType);
    console.log('[GlobalsService] setUserType called with:', userType, '-> parsed as:', typeNum);

    const isAdmin = GlobalsService.isAdminTypeId(typeNum);
    const isSupport = GlobalsService.isStaffTypeId(typeNum);
    const isDesigner = isSupport || typeNum === GlobalsService.USER_TYPE.DESIGNER;
    const isProduction = typeNum === GlobalsService.USER_TYPE.ADMIN || typeNum === GlobalsService.USER_TYPE.PRODUCTION;

    console.log('[GlobalsService] Setting permissions - isAdmin:', isAdmin, 'isSupport:', isSupport, 'isDesigner:', isDesigner);

    this._isAdmin.next(isAdmin);
    this._isSupport.next(isSupport);
    this._isDesigner.next(isDesigner);
    this._isProduction.next(isProduction);
  }

  /**
   * Canonical typeId map. Values come from the backend's user_type table —
   * keep this in sync with backend. This is the only typeId source for the
   * frontend; an old `UserType` enum in core/models/user.model.ts that
   * disagreed with these values was deleted during the support refactor.
   */
  static readonly USER_TYPE = {
    UNDEFINED: 0,
    NORMAL: 1,
    DESIGNER: 2,
    SUPPORT: 3,
    ADMIN: 4,
    PRODUCTION: 5,
    CUSTOMER_SUPPORT: 6
  } as const;

  /**
   * Whether the given typeId is "staff" (ADMIN, SUPPORT, CUSTOMER_SUPPORT,
   * or PRODUCTION). Matches the same set setUserType maps to
   * `_isSupport = true`. Use this when checking a typeId that isn't the
   * *active* user's — e.g. the parked admin during impersonation, which
   * the live BehaviorSubjects don't see.
   */
  static isStaffTypeId(typeId: number | null | undefined): boolean {
    if (typeId == null) return false;
    const t = Number(typeId);
    return t === GlobalsService.USER_TYPE.ADMIN
        || t === GlobalsService.USER_TYPE.SUPPORT
        || t === GlobalsService.USER_TYPE.CUSTOMER_SUPPORT
        || t === GlobalsService.USER_TYPE.PRODUCTION;
  }

  /**
   * Whether the given typeId is "admin" (ADMIN or PRODUCTION).
   * Mirrors setUserType's adminTypes set.
   */
  static isAdminTypeId(typeId: number | null | undefined): boolean {
    if (typeId == null) return false;
    const t = Number(typeId);
    return t === GlobalsService.USER_TYPE.ADMIN
        || t === GlobalsService.USER_TYPE.PRODUCTION;
  }

  /** Instance helper for callers holding the service. Delegates to the static. */
  isStaffTypeId(typeId: number | null | undefined): boolean {
    return GlobalsService.isStaffTypeId(typeId);
  }

  setBrands(brands: Array<string | { name?: string; code?: string; slug?: string }>): void {
    console.log('[GlobalsService] setBrands called with:', brands);

    // If no brands provided, default to Dreambooks Pro
    if (!brands || brands.length === 0) {
      console.log('[GlobalsService] No brands - defaulting to ALL brands enabled');
      this._hasDreambooksPro.next(true);
      this._hasOneLabPro.next(true);
      this._hasLfmPro.next(true);
      this._config.update(cfg => ({
        ...cfg,
        isDreambooksPro: true,
        isOnelabPro: true,
        isLfmPro: true
      }));
      return;
    }

    let hasDreambooksPro = false;
    let hasOneLabPro = false;
    let hasLfmPro = false;

    for (const brand of brands) {
      // Brands may arrive as plain strings (legacy) or objects
      // ({ id, name }, e.g. { name: 'DREAMBOOKSPRO' }) — normalize to the
      // matchable name string either way.
      const raw = typeof brand === 'string'
        ? brand
        : (brand?.name ?? brand?.code ?? brand?.slug ?? '');
      const lowerBrand = String(raw).toLowerCase();
      if (lowerBrand.includes('dreambooks') || lowerBrand.includes('dream')) {
        hasDreambooksPro = true;
      }
      if (lowerBrand.includes('onelab') || lowerBrand.includes('one')) {
        hasOneLabPro = true;
      }
      if (lowerBrand.includes('lfm')) {
        hasLfmPro = true;
      }
    }

    // If nothing matched, default to Dreambooks
    if (!hasDreambooksPro && !hasOneLabPro && !hasLfmPro) {
      console.log('[GlobalsService] No brands matched - defaulting to DreambooksPro');
      hasDreambooksPro = true;
    }

    console.log('[GlobalsService] Brands result:', { hasDreambooksPro, hasOneLabPro, hasLfmPro });

    this._hasDreambooksPro.next(hasDreambooksPro);
    this._hasOneLabPro.next(hasOneLabPro);
    this._hasLfmPro.next(hasLfmPro);

    // Update config signal
    this._config.update(cfg => ({
      ...cfg,
      isDreambooksPro: hasDreambooksPro,
      isOnelabPro: hasOneLabPro,
      isLfmPro: hasLfmPro
    }));
  }

  setBrand(brand: string): void {
    this._config.update(cfg => ({ ...cfg, brand }));
  }

  getImageUrl(image: any): string {
    if (!image) return '';

    if (image.url) {
      if (image.url.startsWith('http')) {
        return image.noCache ? `${image.url}?nocache=${image.noCache}` : image.url;
      }
      return `${this.storageUrl}${image.url}${image.noCache ? '?nocache=' + image.noCache : ''}`;
    }
    return '';
  }

  getThumbUrl(image: any): string {
    if (!image) return '';

    if (image.thumb) {
      if (image.thumb.startsWith('http')) {
        return image.thumb;
      }
      return `${this.storageUrl}${image.thumb}`;
    }
    return this.getImageUrl(image);
  }

  get dreambooksApiToken(): string | undefined {
    return this._config().dreambooksApiToken;
  }

  setDreambooksApiToken(token: string): void {
    this._config.update(cfg => ({ ...cfg, dreambooksApiToken: token }));
  }
}
